import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Formik, Form, ErrorMessage, Field } from "formik";
import * as Yup from "yup";
import {
  create_tier4_interpreter,
  list_interpreters,
  get_interpreter_tiers,
} from "../../api/InterpreterApi";
import {
  Plus,
  Trash2,
  ChevronRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Undo,
  Redo,
} from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "../../components/ui/breadcrumb";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "../../lib/utils";
import countries from "../../assets/countries.json";
import { GlobalContext } from "../../App";
const extensions = [StarterKit];
const validationSchemaBase = {
  phone_number: Yup.string()
    .matches(
      /^\+[1-9]\d{1,14}$/,
      "Phone number must be in valid E.164 format starting with '+'"
    )
    .nullable(),
  interpreter_home_number: Yup.string()
    .matches(
      /^\+[1-9]\d{1,14}$/,
      "Home number must be in valid E.164 format starting with '+'"
    )
    .nullable(),
  country: Yup.string(),
  notes: Yup.string(),
  video_rate: Yup.number()
    .typeError("Video rate must be a number")
    .min(0, "Video rate cannot be negative"),
  tierRates: Yup.object().shape({
    1: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 1 rate must be a number")
        .min(0, "Tier 1 rate cannot be negative")
        .required("Tier 1 rate is required"),
    }),
    2: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 2 rate must be a number")
        .min(0, "Tier 2 rate cannot be negative")
        .required("Tier 2 rate is required"),
    }),
    3: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 3 rate must be a number")
        .min(0, "Tier 3 rate cannot be negative")
        .required("Tier 3 rate is required"),
    }),
    4: Yup.object().shape({
      voice_rate: Yup.number()
        .typeError("Tier 4 rate must be a number")
        .min(0, "Tier 4 rate cannot be negative")
        .required("Tier 4 rate is required"),
    }),
  }),
};

const existingSchema = Yup.object().shape({
  interpreter_special_id: Yup.string().required("Interpreter ID is required"),
  ...validationSchemaBase,
});

const newSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
  interpreter_special_id: Yup.string().required("Interpreter ID is required"),
  languages: Yup.array()
    .min(1, "At least one language must be selected")
    .required("Languages are required"),
  ...validationSchemaBase,
});

const TiptapMenuBar = ({ editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2 p-2 border-b border-gray-200 bg-gray-50 rounded-t-md">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("bold") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={editor.isActive("italic") ? "bg-gray-200" : ""}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 1 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 2 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={
          editor.isActive("heading", { level: 3 }) ? "bg-gray-200" : ""
        }
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
      >
        <Undo className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
      >
        <Redo className="h-4 w-4" />
      </Button>
    </div>
  );
};

const Tiptap = ({ value, onChange }) => {
  const editor = useEditor({
    extensions,
    content: value || "<p></p>",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  return (
    <div className="border rounded-md">
      <TiptapMenuBar editor={editor} />
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4 min-h-[200px] outline-none w-full"
      />
    </div>
  );
};

const ExistingInterpreterForm = ({
  zoomUsers,
  isLoadingZoomUsers,
  onSubmit,
  navigate,
}) => {
  const [openSelector, setOpenSelector] = useState(false);
  const [openCountrySelector, setOpenCountrySelector] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const { setToastOpen, setToastMessage, setToastSeverity } =
    useContext(GlobalContext);

  return (
    <Formik
      initialValues={{
        interpreter_special_id: "",
        phone_number: "",
        interpreter_home_number: "",
        country: "",
        video_rate: "",
        notes: "",
        tierRates: {
          1: { voice_rate: "", video_rate: "" },
          2: { voice_rate: "", video_rate: "" },
          3: { voice_rate: "", video_rate: "" },
          4: { voice_rate: "", video_rate: "" },
        },
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={existingSchema}
      onSubmit={(values, { setSubmitting }) => {
        const data = {
          interpreter_special_id: values.interpreter_special_id,
          interpreter_phone_number: values.phone_number,
          interpreter_home_number: values.interpreter_home_number || null,
          interpreter_country: values.country,
          video_rate: values.video_rate,
          tier1_voice_rate: values.tierRates[1].voice_rate,
          tier2_voice_rate: values.tierRates[2].voice_rate,
          tier3_voice_rate: values.tierRates[3].voice_rate,
          tier4_voice_rate: values.tierRates[4].voice_rate,
          tier1_video_rate: values.tierRates[1].video_rate,
          tier2_video_rate: values.tierRates[2].video_rate,
          tier3_video_rate: values.tierRates[3].video_rate,
          tier4_video_rate: values.tierRates[4].video_rate,
          interpreter_address: values.interpreter_address,
          interpreter_city: values.interpreter_city,
          interpreter_state: values.interpreter_state,
          interpreter_zip: values.interpreter_zip,
          zoom_cc_user_option: "existing",
          zoom_cc_user_id: selectedUser?.interpreter_id || null,
          interpreter_first_name: null,
          interpreter_last_name: null,
          languages: null,
          interpreter_tier: "4",
          interpreter_notes: values.notes || null,
        };
        onSubmit(data, setSubmitting);
      }}
    >
      {({
        values,
        setFieldValue,
        handleChange,
        handleBlur,
        isSubmitting,
        errors,
        touched,
      }) => (
        <Form className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Interpreters</label>
              <Popover open={openSelector} onOpenChange={setOpenSelector}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={openSelector}
                    className="w-full justify-between"
                    disabled={isLoadingZoomUsers}
                  >
                    {selectedUser
                      ? `${selectedUser.interpreter_first_name} ${selectedUser.interpreter_last_name}`
                      : "Select Interpreter..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command
                    filter={(value, search, keywords) => {
                      const q = search?.trim()?.toLowerCase() || "";
                      if (!q) return 1;
                      return keywords.some((k) => {
                        if (!k) return false;
                        const lower = k.toLowerCase();
                        return lower === q || lower.includes(q);
                      })
                        ? 1
                        : 0;
                    }}
                  >
                    <CommandInput placeholder="Search Zoom CC Users..." />
                    <CommandEmpty>
                      {isLoadingZoomUsers ? "Loading..." : "No users found."}
                    </CommandEmpty>
                    <CommandList className="max-h-60 overflow-auto">
                      <CommandGroup>
                        {zoomUsers.map((user) => (
                          <CommandItem
                            keywords={[
                              user.interpreter_first_name,
                              user.interpreter_last_name,
                              user.interpreter_special_id,
                            ].filter(Boolean)}
                            key={user.interpreter_id}
                            value={user.interpreter_id}
                            onSelect={async () => {
                              const sel =
                                selectedUser?.interpreter_id ===
                                user.interpreter_id
                                  ? null
                                  : user;
                              setSelectedUser(sel);

                              if (sel) {
                                setFieldValue(
                                  "interpreter_special_id",
                                  sel.interpreter_special_id || ""
                                );
                                setFieldValue(
                                  "phone_number",
                                  sel.interpreter_phone_number || ""
                                );
                                setFieldValue(
                                  "interpreter_home_number",
                                  sel.interpreter_home_number || ""
                                );

                                // Set country if available
                                if (sel.interpreter_country) {
                                  // Check if the country code exists in our countries array
                                  const countryExists = countries.some(
                                    (c) => c.cca2 === sel.interpreter_country
                                  );
                                  if (countryExists) {
                                    setFieldValue(
                                      "country",
                                      sel.interpreter_country
                                    );
                                  } else {
                                    // Try to find a match by name
                                    const foundCountry = countries.find(
                                      (c) =>
                                        c.name.toLowerCase() ===
                                        sel.interpreter_country.toLowerCase()
                                    );
                                    if (foundCountry) {
                                      setFieldValue(
                                        "country",
                                        foundCountry.cca2
                                      );
                                    }
                                  }
                                }

                                // Fetch and set tier rates
                                try {
                                  setLoadingRates(true);
                                  const ratesResponse =
                                    await get_interpreter_tiers(
                                      sel.interpreter_id
                                    );
                                  if (ratesResponse && ratesResponse.data) {
                                    // Initialize default tierRates object
                                    const tierRates = {
                                      1: { voice_rate: "", video_rate: "" },
                                      2: { voice_rate: "", video_rate: "" },
                                      3: { voice_rate: "", video_rate: "" },
                                      4: { voice_rate: "", video_rate: "" },
                                    };

                                    // Process the array of tier records
                                    if (Array.isArray(ratesResponse.data)) {
                                      ratesResponse.data.forEach((tierData) => {
                                        if (
                                          tierData.interpreter_tier >= 1 &&
                                          tierData.interpreter_tier <= 4
                                        ) {
                                          tierRates[tierData.interpreter_tier] =
                                            {
                                              voice_rate:
                                                tierData.voice_rate || "",
                                              video_rate:
                                                tierData.video_rate ?? "",
                                            };
                                        }
                                      });
                                    }

                                    setFieldValue("tierRates", tierRates);
                                    console.log(tierRates);
                                  }
                                } catch (error) {
                                  console.error(
                                    "Error fetching interpreter rates:",
                                    error
                                  );
                                  setToastOpen(true);
                                  setToastMessage(
                                    "Failed to load interpreter rates"
                                  );
                                  setToastSeverity("error");
                                } finally {
                                  setLoadingRates(false);
                                }
                              } else {
                                // Reset fields if no interpreter is selected
                                setFieldValue("interpreter_special_id", "");
                                setFieldValue("phone_number", "");
                                setFieldValue("interpreter_home_number", "");
                              }

                              setOpenSelector(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedUser?.interpreter_id ===
                                  user.interpreter_id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            {user.interpreter_first_name +
                              " " +
                              user.interpreter_last_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="interpreter_special_id"
                  className="text-sm font-medium"
                >
                  Interpreter ID
                </label>
                <Input
                  id="interpreter_special_id"
                  name="interpreter_special_id"
                  placeholder="Interpreter ID"
                  disabled
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.interpreter_special_id}
                  className={
                    errors.interpreter_special_id &&
                    touched.interpreter_special_id
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="interpreter_special_id"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  placeholder="Enter email"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled
                  value={selectedUser?.email || ""}
                  className={
                    errors.email && touched.email ? "border-red-500" : ""
                  }
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>
              {/* Country Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Country</label>
                <Popover
                  open={openCountrySelector}
                  onOpenChange={setOpenCountrySelector}
                >
                  <PopoverTrigger asChild disabled>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openCountrySelector}
                      className="w-full justify-between"
                    >
                      {values.country
                        ? countries.find((c) => c.cca2 === values.country)
                          ? countries.find((c) => c.cca2 === values.country)
                              .name +
                            " " +
                            countries.find((c) => c.cca2 === values.country)
                              .flag
                          : typeof values.country === "object"
                          ? values.country.name + " " + values.country.flag
                          : values.country
                        : "Select country..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                </Popover>
                <ErrorMessage
                  name="country"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="video_rate" className="text-sm font-medium">
                  Video Rate
                </label>
                <Input
                  disabled
                  id="video_rate"
                  name="video_rate"
                  type="number"
                  step="0.01"
                  placeholder="Enter video rate"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.video_rate || selectedUser?.video_rate}
                  className={
                    errors.video_rate && touched.video_rate
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="video_rate"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="phone_number" className="text-sm font-medium">
                  Phone Number
                </label>
                <Input
                  id="phone_number"
                  name="phone_number"
                  placeholder="Enter phone number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.phone_number}
                  className={
                    errors.phone_number && touched.phone_number
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="phone_number"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="interpreter_home_number"
                  className="text-sm font-medium"
                >
                  Home Number (Optional)
                </label>
                <Input
                  id="interpreter_home_number"
                  name="interpreter_home_number"
                  placeholder="Enter home number"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.interpreter_home_number}
                  className={
                    errors.interpreter_home_number &&
                    touched.interpreter_home_number
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="interpreter_home_number"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="interpreter_address"
                  className="text-sm font-medium"
                >
                  Address
                </label>
                <Input
                  id="interpreter_address"
                  name="interpreter_address"
                  placeholder="Interpreter Address"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={
                    values.interpreter_address ||
                    selectedUser?.interpreter_address
                  }
                  className={
                    errors.interpreter_address && touched.interpreter_address
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="interpreter_address"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="interpreter_city"
                  className="text-sm font-medium"
                >
                  Interpreter City
                </label>
                <Input
                  id="interpreter_city"
                  name="interpreter_city"
                  placeholder="Enter interpreter city"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={
                    values.interpreter_city || selectedUser?.interpreter_city
                  }
                  className={
                    errors.interpreter_city && touched.interpreter_city
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="interpreter_city"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="interpreter_state"
                  className="text-sm font-medium"
                >
                  Interpreter State
                </label>
                <Input
                  id="interpreter_state"
                  name="interpreter_state"
                  placeholder="Enter interpreter state"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={
                    values.interpreter_state || selectedUser?.interpreter_state
                  }
                  className={
                    errors.interpreter_state && touched.interpreter_state
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="interpreter_state"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="interpreter_zip"
                  className="text-sm font-medium"
                >
                  Interpreter ZIP
                </label>
                <Input
                  id="interpreter_zip"
                  name="interpreter_zip"
                  placeholder="Enter interpreter zip"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={
                    values.interpreter_zip || selectedUser?.interpreter_zip
                  }
                  className={
                    errors.interpreter_zip && touched.interpreter_zip
                      ? "border-red-500"
                      : ""
                  }
                />
                <ErrorMessage
                  name="interpreter_zip"
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>
            </div>
            {/* <div className="flex items-center gap-2 mt-12">
              <CardTitle>Tier Rates</CardTitle>
              {loadingRates && (
                <div className="flex items-center text-sm text-gray-500">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Loading rates...
                </div>
              )}
            </div> */}
            {/* <div className="grid gap-4 md:grid-cols-1">
              {[1, 2, 3, 4].map((tier) => (
                <div key={tier} className="space-y-2">
                  <Label className="mb-2">
                    Tier {tier} Voice Rate ($/{tier >= 3 ? "min" : "hr"})
                  </Label>
                  <Field name={`tierRates[${tier}].voice_rate`}>
                    {({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name={`tierRates[${tier}].voice_rate`}
                    component="div"
                    className="text-xs text-red-500 font-light"
                  />
                </div>
              ))}
            </div> */}

            <CardTitle className="mt-12">Notes</CardTitle>
            <div className="mt-4">
              <Field name="notes">
                {({ field, form }) => (
                  <Tiptap
                    value={field.value}
                    onChange={(newValue) =>
                      form.setFieldValue("notes", newValue)
                    }
                  />
                )}
              </Field>
            </div>
          </div>

          <div className="fixed bottom-0 left-0 right-0 w-full bg-white p-4 flex justify-end space-x-4 mt-6 border-t border-gray-200 shadow-lg z-[1000]">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/interpreters")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Add Interpreter"}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const NewInterpreterForm = ({ languages, onSubmit, navigate }) => {
  const [openLanguageSelector, setOpenLanguageSelector] = useState(false);
  const [openCountrySelector, setOpenCountrySelector] = useState(false);

  return (
    <Formik
      initialValues={{
        first_name: "",
        last_name: "",
        email: "",
        phone_number: "",
        interpreter_home_number: "",
        interpreter_special_id: "",
        languages: [],
        country: "",
        video_rate: "",
        notes: "",
        tierRates: {
          1: { voice_rate: "", video_rate: "" },
          2: { voice_rate: "", video_rate: "" },
          3: { voice_rate: "", video_rate: "" },
          4: { voice_rate: "", video_rate: "" },
        },
      }}
      validationSchema={newSchema}
      validateOnBlur={false}
      validateOnChange={false}
      onSubmit={(values, { setSubmitting }) => {
        const data = {
          interpreter_first_name: values.first_name,
          interpreter_last_name: values.last_name,
          interpreter_email: values.email,
          interpreter_phone_number: values.phone_number,
          interpreter_home_number: values.interpreter_home_number || null,
          interpreter_special_id: values.interpreter_special_id,
          languages: JSON.stringify(values.languages),
          interpreter_country: values.country,
          interpreter_tier: "4",
          tier1_voice_rate: values.tierRates[1].voice_rate,
          tier2_voice_rate: values.tierRates[2].voice_rate,
          tier3_voice_rate: values.tierRates[3].voice_rate,
          tier4_voice_rate: values.tierRates[4].voice_rate,
          tier1_video_rate: values.tierRates[1].video_rate,
          tier2_video_rate: values.tierRates[2].video_rate,
          tier3_video_rate: values.tierRates[3].video_rate,
          tier4_video_rate: values.tierRates[4].video_rate,
          video_rate: values.video_rate,
          zoom_cc_user_option: "new",
          zoom_cc_user_id: null,
          interpreter_notes: values.notes || null,
          interpreter_address: values.interpreter_address,
          interpreter_city: values.interpreter_city,
          interpreter_state: values.interpreter_state,
          interpreter_zip: values.interpreter_zip,
        };
        onSubmit(data, setSubmitting);
      }}
    >
      {({
        values,
        setFieldValue,
        handleChange,
        handleBlur,
        isSubmitting,
        errors,
        touched,
      }) => (
        <Form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="first_name" className="text-sm font-medium">
                First Name
              </label>
              <Input
                id="first_name"
                name="first_name"
                placeholder="Enter first name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.first_name}
                className={
                  errors.first_name && touched.first_name
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="first_name"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="last_name" className="text-sm font-medium">
                Last Name
              </label>
              <Input
                id="last_name"
                name="last_name"
                placeholder="Enter last name"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.last_name}
                className={
                  errors.last_name && touched.last_name ? "border-red-500" : ""
                }
              />
              <ErrorMessage
                name="last_name"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                name="email"
                placeholder="Enter email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                className={
                  errors.email && touched.email ? "border-red-500" : ""
                }
              />
              <ErrorMessage
                name="email"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="phone_number" className="text-sm font-medium">
                Phone Number
              </label>
              <Input
                id="phone_number"
                name="phone_number"
                placeholder="Enter phone number"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.phone_number}
                className={
                  errors.phone_number && touched.phone_number
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="phone_number"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="interpreter_home_number"
                className="text-sm font-medium"
              >
                Home Number (Optional)
              </label>
              <Input
                id="interpreter_home_number"
                name="interpreter_home_number"
                placeholder="Enter home number"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.interpreter_home_number}
                className={
                  errors.interpreter_home_number &&
                  touched.interpreter_home_number
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="interpreter_home_number"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="interpreter_special_id"
                className="text-sm font-medium"
              >
                Interpreter ID
              </label>
              <Input
                id="interpreter_special_id"
                name="interpreter_special_id"
                placeholder="Enter interpreter ID"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.interpreter_special_id}
                className={
                  errors.interpreter_special_id &&
                  touched.interpreter_special_id
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="interpreter_special_id"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            {/* <div className="space-y-2">
              <label htmlFor="video_rate" className="text-sm font-medium">
                Video Rate
              </label>
              <Input
                id="video_rate"
                name="video_rate"
                type="number"
                step="0.01"
                placeholder="Enter video rate"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.video_rate}
                className={
                  errors.video_rate && touched.video_rate
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="video_rate"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div> */}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label
                htmlFor="interpreter_address"
                className="text-sm font-medium"
              >
                Address
              </label>
              <Input
                id="interpreter_address"
                name="interpreter_address"
                placeholder="Interpreter Address"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.interpreter_address}
                className={
                  errors.interpreter_address && touched.interpreter_address
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="interpreter_address"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="interpreter_city" className="text-sm font-medium">
                Interpreter City
              </label>
              <Input
                id="interpreter_city"
                name="interpreter_city"
                placeholder="Enter interpreter city"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.interpreter_city}
                className={
                  errors.interpreter_city && touched.interpreter_city
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="interpreter_city"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label
                htmlFor="interpreter_state"
                className="text-sm font-medium"
              >
                Interpreter State
              </label>
              <Input
                id="interpreter_state"
                name="interpreter_state"
                placeholder="Enter interpreter state"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.interpreter_state}
                className={
                  errors.interpreter_state && touched.interpreter_state
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="interpreter_state"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="interpreter_zip" className="text-sm font-medium">
                Interpreter ZIP
              </label>
              <Input
                id="interpreter_zip"
                name="interpreter_zip"
                placeholder="Enter interpreter zip"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.interpreter_zip}
                className={
                  errors.interpreter_zip && touched.interpreter_zip
                    ? "border-red-500"
                    : ""
                }
              />
              <ErrorMessage
                name="interpreter_zip"
                component="div"
                className="text-xs text-red-500 font-light"
              />
            </div>
          </div>
          {/* Country Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Country</label>
            <Popover
              open={openCountrySelector}
              onOpenChange={setOpenCountrySelector}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={openCountrySelector}
                  className="w-full justify-between"
                >
                  {values.country
                    ? countries.find((c) => c.cca2 === values.country)
                      ? countries.find((c) => c.cca2 === values.country).name +
                        " " +
                        countries.find((c) => c.cca2 === values.country).flag
                      : typeof values.country === "object"
                      ? values.country.name + " " + values.country.flag
                      : values.country
                    : "Select country..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search countries..." />
                  <CommandEmpty>No country found.</CommandEmpty>
                  <CommandList className="max-h-60 overflow-auto">
                    <CommandGroup>
                      {countries.map((country) => (
                        <CommandItem
                          key={country.cca2}
                          value={country}
                          onSelect={() => {
                            setFieldValue("country", country.cca2);
                            setOpenCountrySelector(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              values.country === country.cca2
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {country.name} {country.flag}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <ErrorMessage
              name="country"
              component="div"
              className="text-xs text-red-500 font-light"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Languages</label>
            <Popover
              open={openLanguageSelector}
              onOpenChange={setOpenLanguageSelector}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={openLanguageSelector}
                  className="w-full justify-between"
                >
                  {values.languages.length > 0
                    ? `${values.languages.length} language(s) selected`
                    : "Select languages..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <Command>
                  <CommandInput placeholder="Search languages..." />
                  <CommandEmpty>No language found.</CommandEmpty>
                  <CommandList className="max-h-60 overflow-auto">
                    <CommandGroup>
                      {languages.map((language) => (
                        <CommandItem
                          key={language.code}
                          value={language.name}
                          onSelect={() => {
                            const sel = values.languages.includes(language.name)
                              ? values.languages.filter(
                                  (n) => n !== language.name
                                )
                              : [...values.languages, language.name];
                            setFieldValue("languages", sel);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              values.languages.includes(language.name)
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                          {language.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <div className="flex flex-wrap gap-2 mt-2">
              {values.languages.map((name) => (
                <div
                  key={name}
                  className="bg-primary/10 text-primary text-xs px-2 py-1 rounded-md flex items-center"
                >
                  {name}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 ml-1"
                    onClick={() =>
                      setFieldValue(
                        "languages",
                        values.languages.filter((n) => n !== name)
                      )
                    }
                  >
                    <span className="sr-only">Remove</span>
                    <span className="text-xs">×</span>
                  </Button>
                </div>
              ))}
            </div>
            <ErrorMessage
              name="languages"
              component="div"
              className="text-xs text-red-500 font-light"
            />
          </div>

          <CardTitle className="mt-12">Voice Rates Per Tier</CardTitle>
          <div className="grid gap-4 md:grid-cols-1">
            {[1, 2, 3, 4].map((tier) => (
              <div key={tier} className="space-y-2">
                <Label className="mb-2">
                  Tier {tier} Voice Rate ($/{tier >= 3 ? "min" : "hr"})
                </Label>
                <Field name={`tierRates.${tier}.voice_rate`}>
                  {({ field, form }) => (
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      onChange={(e) => {
                        const value =
                          e.target.value === ""
                            ? ""
                            : parseFloat(e.target.value);
                        form.setFieldValue(
                          `tierRates.${tier}.voice_rate`,
                          isNaN(value) ? "" : value
                        );
                      }}
                      onBlur={() =>
                        form.setFieldTouched(
                          `tierRates.${tier}.voice_rate`,
                          true
                        )
                      }
                      className={
                        form.touched.tierRates?.[tier]?.voice_rate &&
                        form.errors.tierRates?.[tier]?.voice_rate
                          ? "border-red-500"
                          : ""
                      }
                    />
                  )}
                </Field>
                <ErrorMessage
                  name={`tierRates.${tier}.voice_rate`}
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>
            ))}
          </div>

          <CardTitle className="mt-12">Video Rates Per Tier</CardTitle>
          <div className="grid gap-4 md:grid-cols-1">
            {[1, 2, 3].map((tier) => (
              <div key={tier} className="space-y-2">
                <Label className="mb-2">
                  Tier {tier} Video Rate ($/{tier >= 3 ? "min" : "hr"})
                </Label>
                <Field name={`tierRates.${tier}.video_rate`}>
                  {({ field, form }) => (
                    <Input
                      {...field}
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      onChange={(e) => {
                        const value =
                          e.target.value === ""
                            ? ""
                            : parseFloat(e.target.value);
                        form.setFieldValue(
                          `tierRates.${tier}.video_rate`,
                          isNaN(value) ? "" : value
                        );
                      }}
                      onBlur={() =>
                        form.setFieldTouched(
                          `tierRates.${tier}.video_rate`,
                          true
                        )
                      }
                      className={
                        form.touched.tierRates?.[tier]?.video_rate &&
                        form.errors.tierRates?.[tier]?.video_rate
                          ? "border-red-500"
                          : ""
                      }
                    />
                  )}
                </Field>
                <ErrorMessage
                  name={`tierRates.${tier}.video_rate`}
                  component="div"
                  className="text-xs text-red-500 font-light"
                />
              </div>
            ))}
          </div>


          <div className="mt-8">
            <CardTitle className="mb-4">Notes</CardTitle>
            <Field name="notes">
              {({ field, form }) => (
                <Tiptap
                  value={field.value}
                  onChange={(newValue) => form.setFieldValue("notes", newValue)}
                />
              )}
            </Field>
          </div>

          <div className="fixed bottom-0 left-0 right-0 w-full bg-white p-4 flex justify-end space-x-4 mt-6 border-t border-gray-200 shadow-lg z-[1000]">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/interpreters")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Add Interpreter"}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

const AddInterpreterTier4FormSplit = () => {
  const navigate = useNavigate();
  const { setToastOpen, setToastMessage, setToastSeverity, languages } =
    useContext(GlobalContext);
  const [zoomUserOption, setZoomUserOption] = useState("existing");
  const [zoomUsers, setZoomUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await list_interpreters();
        setZoomUsers(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const handleCreate = async (data, setSubmitting) => {
    try {
      await create_tier4_interpreter(data);
      setToastOpen(true);
      setToastMessage("Interpreter created successfully");
      setToastSeverity("success");
      navigate("/admin/interpreters");
    } catch (err) {
      setToastOpen(true);
      setToastMessage(err.response.data.message);
      setToastSeverity("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto pb-12">
      <div className="flex items-center justify-between">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate("/admin")}>
                Admin
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbLink onClick={() => navigate("/admin/interpreters")}>
                Interpreters
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator>/</BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>Add Tier 4 Interpreter</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Interpreter Information</CardTitle>
          <CardDescription>
            Add Tier 4 Interpreter details. This info will be synced with Zoom
            Native Address Book
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <RadioGroup
              id="zoom-cc-user-option"
              value={zoomUserOption}
              onValueChange={setZoomUserOption}
              className="flex flex-row"
            >
              <Label
                htmlFor="existing-zoom-user"
                className={`font-normal flex items-center space-x-2 border border-gray-200 rounded-md p-4 flex-1 hover:bg-gray-50 cursor-pointer ${
                  zoomUserOption === "existing"
                    ? "bg-gray-50 border-gray-400"
                    : ""
                }`}
              >
                <RadioGroupItem value="existing" id="existing-zoom-user" />
                Existing Zoom CC User
              </Label>
              <Label
                htmlFor="new-zoom-user"
                className={`font-normal flex items-center space-x-2 border border-gray-200 rounded-md p-4 flex-1 hover:bg-gray-50 cursor-pointer ${
                  zoomUserOption === "new" ? "bg-gray-50 border-gray-400" : ""
                }`}
              >
                <RadioGroupItem value="new" id="new-zoom-user" />
                New Interpreter
              </Label>
            </RadioGroup>

            {zoomUserOption === "existing" ? (
              <ExistingInterpreterForm
                zoomUsers={zoomUsers}
                isLoadingZoomUsers={loadingUsers}
                onSubmit={handleCreate}
                navigate={navigate}
              />
            ) : (
              <NewInterpreterForm
                languages={languages}
                onSubmit={handleCreate}
                navigate={navigate}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddInterpreterTier4FormSplit;
